import { useIntl } from "react-intl";
import dayjs from "dayjs";
import router from "next/router";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { CardCrossIcon } from "@/public/icons/CardCross";
import { BillingDownloadIcon } from "@/public/icons/BillingDownload";
import { CardIcon } from "@/public/icons/CardIconIcon";
// import { DownloadIcon } from "@/public/icons/Download";
import { capitalize } from "@/utils/formatName";
import { sendPostRequest } from "@/utils/sendPostRequest";
import { routes } from "@/routes/routes";
import { InvoiceCreateCacheIdRequest } from "@/interfaces/request/invoiceCreateCacheIdRequest";
import { Loader } from "@/components/common/loader/Loader";
import { PaymentType } from "@/interfaces/payment";
import styles from "./QuickActions.module.scss";

export type QuickActionsProps = {
  month: number;
  year: number;
  count: number;
  unpaidCount: number;
  unpaidTotal: number;
  itemsChecked: string[];
};

export const QuickActions = ({
  month,
  year,
  count,
  unpaidCount,
  unpaidTotal,
  itemsChecked,
}: QuickActionsProps) => {
  const intl = useIntl();

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsDisabled(itemsChecked.length <= 0);
  }, [itemsChecked]);

  const handleOnPayAmountClick = () => {
    if (isDisabled) {
      return;
    }
    setIsLoading(true);

    const token = sessionStorage.getItem("keycloak_token") ?? "";

    const body: InvoiceCreateCacheIdRequest = {
      firstname: sessionStorage.getItem("keycloak_user_fistname") ?? "",
      lastname: sessionStorage.getItem("keycloak_user_lastname") ?? "",
      email: sessionStorage.getItem("keycloak_user_email") ?? "",
      invoiceIds: itemsChecked,
    };
    sendPostRequest({
      url: `/invoices/v1/payment/createCacheId`,
      body: JSON.stringify(body),
      accessToken: token,
      additionalHeaders: [{ name: "Content-Type", value: "application/json" }],
    })
      .then((cacheId: string) => {
        void router.push({
          pathname: routes.paymentPage(cacheId),
          query: {
            paymentType: PaymentType.invoice,
            returnPage: "accountB2B",
            confirmationReturnPage: router.asPath,
            confirmationBackButtonLabel: "backToAccount",
          },
        });
      })
      .catch((e) => {
        console.log(e);
        setIsLoading(false);
      });
  };

  return (
    <div className={styles.quickActions}>
      <div className={styles.unpaid}>
        <div className={styles.title}>
          <div className={styles.icon}>
            <CardCrossIcon />
          </div>
          <div className={styles.label}>
            {intl.formatMessage(
              { id: "unpaidBillsFrom" },
              {
                nb: unpaidCount,
                month: capitalize(dayjs().month(month).format("MMMM")),
                year: year,
              }
            )}
          </div>
        </div>
        <div className={styles.content}>
          <span className={styles.big}>{unpaidCount}</span>
          <span>
            {intl.formatMessage(
              { id: "billingsTotal" },
              { total: unpaidTotal.toFixed(2).replace(/[.,]00$/, "") }
            )}
          </span>
        </div>
        <div
          className={classNames(styles.action, {
            [styles.isDisabled]: isDisabled,
          })}
          onClick={handleOnPayAmountClick}
        >
          {isLoading ? (
            <Loader className={styles.loader} />
          ) : (
            <div className={styles.actionContent}>
              <CardIcon color={isDisabled ? "#dfe1e4" : "#37837D"} />
              {intl.formatMessage(
                { id: "payAmount" },
                { amount: unpaidTotal.toFixed(2).replace(/[.,]00$/, "") }
              )}
            </div>
          )}
        </div>
      </div>
      <div className={styles.total}>
        <div className={styles.title}>
          <div className={styles.icon}>
            <BillingDownloadIcon />
          </div>
          <div className={styles.label}>
            {intl.formatMessage(
              { id: "totalBillsFrom" },
              {
                month: capitalize(dayjs().month(month).format("MMMM")),
                year: year,
              }
            )}
          </div>
        </div>
        <div className={styles.content}>
          <span>{count}</span>
        </div>
        {/* <div className={styles.action}>
          <DownloadIcon />
          {intl.formatMessage({ id: "download" })}
        </div> */}
      </div>
    </div>
  );
};
